import { APIResponse, request } from "./api.js";
import { UnknownError } from "./errors.js";
import { Credentials, getShowLockedParam } from "../common/credentials.js";
import {
  CategoryNode,
  NavigationNode,
  PageNode,
  Styleguide,
  StyleguideVersion,
} from "./types/styleguide.js";
import { captureException } from "../common/errors.js";

interface StyleguideListSuccessResponse {
  styleguides: Styleguide[];
}

export async function listStyleguides(
  credentials: Credentials,
  clientUserAgent?: string,
) {
  try {
    const response = await request<
      APIResponse<StyleguideListSuccessResponse, {}, {}>
    >(
      "/styleguides",
      credentials,
      {
        method: "GET",
      },
      undefined,
      undefined,
      { clientUserAgent },
    );

    if ("styleguides" in response.data) {
      if (credentials.type === "jwt") {
        return response.data.styleguides.filter(
          (sg) => sg.id === credentials.styleguideId,
        );
      }

      return response.data.styleguides;
    }

    throw new UnknownError();
  } catch (e) {
    captureException(e);
    return [];
  }
}

interface StyleguideVersionsSuccessResponse {
  versions: StyleguideVersion[];
}

export async function listStyleguideVersions(
  credentials: Credentials,
  styleguideId: number,
  clientUserAgent?: string,
): Promise<StyleguideVersion[]> {
  const searchParams = new URLSearchParams({ show_hidden: "false" });

  try {
    const response = await request<
      APIResponse<StyleguideVersionsSuccessResponse, {}, {}>
    >(
      `/styleguides/${styleguideId}/versions`,
      credentials,
      { method: "GET" },
      searchParams,
      undefined,
      { clientUserAgent },
    );

    if ("versions" in response.data) {
      return response.data.versions;
    }

    throw new UnknownError();
  } catch (e) {
    captureException(e);
    throw e;
  }
}

interface SyleguideTreeSuccessResponse {
  tree: (PageNode | CategoryNode | NavigationNode)[];
}

export async function getStyleguideTree(
  credentials: Credentials,
  id: number,
  clientUserAgent?: string,
  releaseId?: number,
) {
  const searchParams = new URLSearchParams({
    show_hidden: "false",
    show_locked: getShowLockedParam(credentials),
  });
  if (releaseId) {
    searchParams.set("release_id", String(releaseId));
  }

  try {
    const response = await request<
      APIResponse<SyleguideTreeSuccessResponse, {}, {}>
    >(
      `/styleguides/${id}/tree`,
      credentials,
      {
        method: "GET",
      },
      searchParams,
      undefined,
      { clientUserAgent },
    );

    if ("tree" in response.data) {
      return response.data.tree;
    }

    throw new UnknownError();
  } catch (e) {
    captureException(e);
    throw e;
  }
}
